import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Download, FileDown, LayoutGrid, MapPin, Package, Rows3, Search, Upload } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { TD_DENSITY, TH_DENSITY } from '@/components/ui/tableDensity';
import { Input } from '@/components/form/Input';
import { FormGroup } from '@/components/form/FormGroup';
import { Modal } from '@/components/ui/Modal';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import {
  createLocation,
  deleteLocation,
  getLocationHistory,
  getLocationOccupancy,
  importLocations,
  listLocations,
  locationsExportPath,
  locationsImportTemplatePath,
  updateLocation,
  type Location,
  type LocationLevel,
} from '@/api/locations';
import { LocationLabelModal } from './LocationLabelModal';
import { BulkAddLocationsModal } from './BulkAddLocationsModal';

interface TreeNode extends Location {
  children: TreeNode[];
}

function buildTree(rows: Location[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  rows.forEach((r) => byId.set(r.id, { ...r, children: [] }));
  const roots: TreeNode[] = [];
  byId.forEach((n) => {
    if (n.parentId && byId.has(n.parentId)) byId.get(n.parentId)!.children.push(n);
    else roots.push(n);
  });
  const sort = (list: TreeNode[]) => {
    list.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    list.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

const LEVEL_ICON_COLOR: Record<LocationLevel, string> = {
  Zone: 'text-info',
  Aisle: 'text-[#8B5CF6]',
  Rack: 'text-success',
  Bin: 'text-accent',
};

const CHILD_LABEL: Record<LocationLevel, string> = {
  Zone: 'aisles',
  Aisle: 'racks',
  Rack: 'bins',
  Bin: '',
};

const SECTION_BAR = 'flex items-center justify-between gap-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]';
const DETAIL_TH = `border-b-2 border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wide text-accent whitespace-nowrap ${TH_DENSITY} !px-4`;
const DETAIL_TD = `font-semibold text-[var(--text-primary)] whitespace-nowrap ${TD_DENSITY} !px-4`;

function LevelIcon({ level, className = '' }: { level: LocationLevel; className?: string }) {
  const cls = `${LEVEL_ICON_COLOR[level]} ${className}`.trim();
  switch (level) {
    case 'Zone':  return <Boxes size={14} className={cls} />;
    case 'Aisle': return <Rows3 size={14} className={cls} />;
    case 'Rack':  return <LayoutGrid size={14} className={cls} />;
    case 'Bin':   return <Package size={14} className={cls} />;
  }
}

export function LocationMasterPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const fe = useFieldErrors<'code' | 'name'>();
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formParent, setFormParent] = useState<Location | null>(null); // null = flat root-level location
  const [formLevel, setFormLevel] = useState<LocationLevel>('Rack');
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkParent, setBulkParent] = useState<Location | null>(null);
  const [bulkLevel, setBulkLevel] = useState<LocationLevel>('Rack');

  const [labelFor, setLabelFor] = useState<Location | null>(null);

  const query = useQuery({
    queryKey: ['locations', { search, includeInactive }],
    queryFn: () => listLocations({ search: search || undefined, includeInactive }),
    staleTime: 10_000,
  });
  const rows = query.data ?? [];
  const tree = useMemo(() => buildTree(rows), [rows]);
  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  const occQuery = useQuery({
    queryKey: ['location-occupancy', selectedId],
    queryFn: () => getLocationOccupancy(selectedId!),
    enabled: !!selectedId,
    staleTime: 5_000,
  });

  const historyQuery = useQuery({
    queryKey: ['location-history', selectedId],
    queryFn: () => getLocationHistory(selectedId!),
    enabled: !!selectedId,
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: createLocation,
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      setFormOpen(false);
      setSelectedId(row.id);
      toast.push(`Created ${row.level} "${row.code}"`, 'success');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Create failed'), 'error'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...dto }: { id: number; code?: string; name?: string; active?: boolean }) =>
      updateLocation(id, dto),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      setFormOpen(false);
      toast.push(`Updated "${row.code}"`, 'success');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Update failed'), 'error'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteLocation,
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      if (selectedId === row.id) setSelectedId(null);
      toast.push(row.active ? `Deleted "${row.code}"` : `Deactivated "${row.code}"`, 'info');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  function openCreateRoot() {
    setFormMode('create');
    setFormParent(null);
    setFormLevel('Rack');
    setFormCode('');
    setFormName('');
    setFormOpen(true);
  }

  function openEdit(row: Location) {
    setFormMode('edit');
    setFormParent(null);
    setFormLevel(row.level as LocationLevel);
    setFormCode(row.code);
    setFormName(row.name);
    setSelectedId(row.id);
    setFormOpen(true);
  }

  function openBulkRoot() {
    setBulkParent(null);
    setBulkLevel('Rack');
    setBulkOpen(true);
  }

  function submitForm() {
    if (!fe.validate({
      code: !formCode.trim() && 'Code is required',
      name: !formName.trim() && 'Name is required',
    })) return;
    if (formMode === 'create') {
      createMut.mutate({
        code: formCode.trim(),
        name: formName.trim(),
        level: formLevel,
        parentId: formParent?.id ?? null,
      });
    } else if (selected) {
      updateMut.mutate({
        id: selected.id,
        code: formCode.trim() !== selected.code ? formCode.trim() : undefined,
        name: formName.trim() !== selected.name ? formName.trim() : undefined,
      });
    }
  }

  function toggleActive(row: Location) {
    updateMut.mutate({ id: row.id, active: !row.active });
  }

  async function requestDelete(row: Location) {
    if (!await confirmDlg(`Delete "${row.code}"? (Will be deactivated if it has stock history.)`)) return;
    deleteMut.mutate(row.id);
  }

  return (
    <div className="flex h-full flex-col">
      <SectionHeader
        title="Location Master"      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8"
              aria-label="Search locations by code or name"
            />
          </div>
        </FilterField>
        <FilterField label="Show inactive">
          <label className="inline-flex h-6 items-center gap-1.5 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] sm:h-8 sm:px-3 sm:text-xs">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-3 w-3 accent-accent"
            />
            Inactive
          </label>
        </FilterField>
        {/* Merged chip strip: visible on both viewports, right-aligned inside the filter block. */}
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto [&>*]:whitespace-nowrap sm:col-span-1 sm:ml-auto sm:items-end sm:self-end sm:[&>*]:flex-none">
          <button
            type="button"
            onClick={() => downloadExcel(locationsExportPath({ search: search || undefined, includeInactive }), 'GRHAMOY_Locations.xlsx')}
            title="Export current filtered list to Excel"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button
            type="button"
            onClick={() => downloadExcel(locationsImportTemplatePath(), 'GRHAMOY_Locations_Template.xlsx')}
            title="Download a sample Excel with the correct columns and one example row"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            title="Import locations from Excel"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <Upload size={12} /> Import
          </button>
          <button
            type="button"
            onClick={openBulkRoot}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            Bulk Add
          </button>
          <button
            type="button"
            onClick={openCreateRoot}
            className="inline-flex h-7 items-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:h-8 sm:px-3 sm:text-xs"
          >
            ＋ New Location
          </button>
        </div>
      </FilterBar>

      <div className="grid grid-cols-1 gap-3 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(340px,1fr)_1.6fr]">
        {/* Tree */}
        <div className="flex flex-col overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl lg:min-h-0">
          <div className="flex min-h-[44px] shrink-0 items-center justify-between gap-2 border-b border-[var(--border-color)] px-4 py-2">
            <span className="flex items-center gap-2 text-sm font-extrabold">
              <MapPin size={14} className="text-accent" /> Locations
            </span>
            <span className="text-xs text-[var(--text-muted)]">{rows.length} total</span>
          </div>

          <div className="max-h-[60vh] overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]">
            {query.isLoading && <div className="px-4 py-8 text-center text-sm text-[var(--text-muted)]">Loading…</div>}
            {!query.isLoading && tree.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-[var(--text-muted)]">
                No locations yet. Click "+ New Location" to start.
              </div>
            )}
            {tree.map((n) => (
              <TreeRow
                key={n.id}
                node={n}
                depth={0}
                selectedId={selectedId}
                collapsed={collapsed}
                onToggle={(id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }))}
                onSelect={setSelectedId}
              />
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="flex flex-col overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl lg:min-h-0">
          {!selected && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 py-12 text-center">
              <MapPin size={30} className="text-[var(--text-muted)] opacity-40" />
              <div className="text-[12px] text-[var(--text-muted)]">Select a location on the left to see its details, occupancy and history.</div>
            </div>
          )}

          {selected && (
            <div className="flex flex-col lg:min-h-0 lg:flex-1">
              {/* Header */}
              <div className="flex min-h-[44px] shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[var(--border-color)] px-4 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <LevelIcon level={selected.level as LocationLevel} />
                  <span className="text-sm font-extrabold text-accent">{selected.code}</span>
                  {selected.name !== selected.code && <span className="truncate text-xs text-[var(--text-muted)]">{selected.name}</span>}
                  {!selected.active && (
                    <span className="inline-flex items-center rounded-full border border-[var(--border-color)] bg-[var(--bg-soft)] px-2 py-0.5 text-[10px] font-bold uppercase text-[var(--text-muted)]">
                      Inactive
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-1.5">
                  <Button size="sm" variant="glass" onClick={() => setLabelFor(selected)}>
                    Print QR
                  </Button>
                  <Button size="sm" variant="glass" onClick={() => openEdit(selected)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={selected.active ? 'warning' : 'success'}
                    onClick={() => toggleActive(selected)}
                  >
                    {selected.active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => requestDelete(selected)}>
                    Delete
                  </Button>
                </div>
              </div>

              <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]">
                {/* Occupancy */}
                <div className={SECTION_BAR}>
                  <span>Current occupancy</span>
                  <span className="font-semibold normal-case tracking-normal">{occQuery.data?.total ?? 0} units</span>
                </div>
                {occQuery.data && occQuery.data.byItem.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={`${DETAIL_TH} text-left`}>Item Code</th>
                        <th className={`${DETAIL_TH} text-left`}>Item Name</th>
                        <th className={`${DETAIL_TH} text-right`}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {occQuery.data.byItem.map((r) => (
                        <tr key={r.itemId} className="border-b border-[var(--border-color)] odd:bg-[var(--bg-secondary)]/60">
                          <td className={`${DETAIL_TD} font-extrabold text-accent`}>{r.code}</td>
                          <td className={`${DETAIL_TD} font-bold`}>{r.name}</td>
                          <td className={`${DETAIL_TD} text-right font-bold`}>{r.qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-6 text-center text-xs text-[var(--text-muted)]">Empty — no serials placed here.</div>
                )}

                {/* History */}
                <div className={`${SECTION_BAR} border-t`}>
                  <span>Assignment history</span>
                  {historyQuery.data && historyQuery.data.length > 0 && (
                    <span className="font-semibold normal-case tracking-normal">{historyQuery.data.length} record{historyQuery.data.length === 1 ? '' : 's'}</span>
                  )}
                </div>
                {historyQuery.data && historyQuery.data.length > 0 ? (
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={`${DETAIL_TH} text-left`}>Serial</th>
                        <th className={`${DETAIL_TH} text-left`}>Placed</th>
                        <th className={`${DETAIL_TH} text-left`}>Removed</th>
                        <th className={`${DETAIL_TH} text-left`}>Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyQuery.data.map((r) => (
                        <tr key={r.id} className="border-b border-[var(--border-color)] odd:bg-[var(--bg-secondary)]/60">
                          <td className={`${DETAIL_TD} font-extrabold text-accent`}>{r.serial.serialNo}</td>
                          <td className={DETAIL_TD}>{new Date(r.placedAt).toLocaleString()}</td>
                          <td className={DETAIL_TD}>
                            {r.removedAt ? new Date(r.removedAt).toLocaleString() : (
                              <span className="font-bold text-success">In place</span>
                            )}
                          </td>
                          <td className={`${DETAIL_TD} text-[var(--text-muted)]`}>{r.removedByRef ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-6 text-center text-xs text-[var(--text-muted)]">No assignments yet.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={formMode === 'create' ? 'New Location' : 'Edit Location'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={submitForm} disabled={createMut.isPending || updateMut.isPending}>
              {formMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <FormGroup label="Code (scannable)" required error={fe.errors.code}>
            <Input
              invalid={fe.has('code')}
              value={formCode}
              onChange={(e) => { setFormCode(e.target.value); fe.clear('code'); }}
              placeholder="e.g. RACK-001, SHELF-A5"
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Name" required error={fe.errors.name}>
            <Input
              invalid={fe.has('name')}
              value={formName}
              onChange={(e) => { setFormName(e.target.value); fe.clear('name'); }}
              placeholder="Human-friendly name"
            />
          </FormGroup>
        </div>
      </Modal>

      <LocationLabelModal
        open={!!labelFor}
        location={labelFor}
        onClose={() => setLabelFor(null)}
      />

      <BulkAddLocationsModal
        open={bulkOpen}
        parent={bulkParent}
        level={bulkLevel}
        onClose={() => setBulkOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['locations'] })}
      />

      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Locations"
        expectedColumns={['Code*', 'Name', 'Level', 'Parent Code', 'Capacity', 'Active']}
        onImport={importLocations}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['locations'] })}
      />
    </div>
  );
}

function TreeRow({
  node,
  depth,
  selectedId,
  collapsed,
  onToggle,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedId: number | null;
  collapsed: Record<number, boolean>;
  onToggle: (id: number) => void;
  onSelect: (id: number) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = !collapsed[node.id];
  const isSelected = selectedId === node.id;
  const level = node.level as LocationLevel;
  const childLabel = CHILD_LABEL[level];

  const totalDescendants = countDescendants(node);

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(node.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(node.id); }
          if (e.key === 'ArrowRight' && hasChildren && !isOpen) onToggle(node.id);
          if (e.key === 'ArrowLeft' && hasChildren && isOpen) onToggle(node.id);
        }}
        className={
          'group relative flex h-9 cursor-pointer items-center gap-2 border-b border-[var(--border-color)] pr-3 text-[13px] transition ' +
          (isSelected ? 'bg-accent/10' : 'hover:bg-[var(--bg-secondary)]')
        }
        style={{ paddingLeft: 10 + depth * 18 }}
      >
        {/* Selected accent bar */}
        {isSelected && <span className="absolute inset-y-0 left-0 w-0.5 bg-accent" />}

        {/* Disclosure caret */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.id);
          }}
          className={
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ' +
            (hasChildren ? 'text-[var(--text-secondary)] hover:bg-[var(--bg-soft)]' : 'text-transparent cursor-default')
          }
          tabIndex={-1}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {hasChildren ? (
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 150ms' }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          ) : (
            <span className="h-1 w-1 rounded-full bg-[var(--text-muted)]/40" />
          )}
        </button>

        {/* Level icon */}
        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
          <LevelIcon level={level} />
        </span>

        {/* Code + name (the name is shown only when it says more than the code) */}
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="shrink-0 font-extrabold text-accent">{node.code}</span>
          {node.name !== node.code && <span className="truncate text-[11px] text-[var(--text-muted)]">{node.name}</span>}
        </div>

        {/* Child count chip */}
        {hasChildren && childLabel && (
          <span
            className="flex-shrink-0 rounded-full border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]"
            title={
              totalDescendants > node.children.length
                ? `${node.children.length} direct ${childLabel} · ${totalDescendants} total descendants`
                : `${node.children.length} ${childLabel}`
            }
          >
            {node.children.length} {childLabel}
          </span>
        )}

        {!node.active && (
          <span className="flex-shrink-0 rounded-full border border-[var(--border-color)] bg-[var(--bg-soft)] px-2 py-0.5 text-[9px] font-bold uppercase text-[var(--text-muted)]">
            inactive
          </span>
        )}
      </div>

      {hasChildren && isOpen && (
        <div className="relative">
          {/* Vertical guide line */}
          <span
            className="absolute inset-y-0 w-px bg-[var(--border-color)]"
            style={{ left: depth * 18 + 20 }}
            aria-hidden
          />
          {node.children.map((c) => (
            <TreeRow
              key={c.id}
              node={c}
              depth={depth + 1}
              selectedId={selectedId}
              collapsed={collapsed}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function countDescendants(node: TreeNode): number {
  return node.children.reduce((acc, c) => acc + 1 + countDescendants(c), 0);
}
